/**
 * Vault Venues Hook
 *
 * Provides venue management functions using the vault backend.
 * All operations require authentication via the useAuth hook.
 */

import { useState, useCallback } from 'react';
import { useAuth } from './auth-context';
import type { Venue, VenueFormData, NewVenueFormData, DayHours, VenuePhoto, VenueDocument, VenueRagUrl } from '@/types/venue';

// API response types
interface VenueResponse {
  venue: Venue;
}

interface VenuesListResponse {
  venues: Venue[];
}

interface FileUploadResponse {
  file: {
    id: string;
    url: string;
    vaultUri?: string;
    directUrl?: string;
    filename: string;
    type: 'photo' | 'document';
    mimeType: string;
    size: number;
    uploadedAt: string;
  };
}

interface ApiError {
  error: string;
}

// Hook return type
interface UseVaultVenuesReturn {
  // State
  venues: Venue[];
  currentVenue: Venue | null;
  loading: boolean;
  error: string | null;

  // Operations
  listVenues: () => Promise<Venue[]>;
  getVenue: (id: string) => Promise<Venue>;
  createVenue: (data: NewVenueFormData | (VenueFormData & { hours?: DayHours[]; photos?: VenuePhoto[]; documents?: VenueDocument[]; ragUrls?: VenueRagUrl[] })) => Promise<Venue>;
  updateVenue: (id: string, data: Partial<VenueFormData> & { hours?: DayHours[]; photos?: VenuePhoto[]; documents?: VenueDocument[]; ragUrls?: VenueRagUrl[] }) => Promise<Venue>;
  deleteVenue: (id: string) => Promise<void>;

  // File operations
  uploadFile: (file: File, fileType: 'photo' | 'document', venueId?: string) => Promise<FileUploadResponse['file']>;

  // Helpers
  clearError: () => void;
  clearCurrentVenue: () => void;
}

const API_BASE = '/api/vault/venues';

export function useVaultVenues(): UseVaultVenuesReturn {
  const { session, isAuthenticated } = useAuth();
  const [venues, setVenues] = useState<Venue[]>([]);
  const [currentVenue, setCurrentVenue] = useState<Venue | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Make an authenticated API request
   */
  const apiRequest = useCallback(
    async <T>(
      path: string,
      options: RequestInit = {}
    ): Promise<T> => {
      if (!isAuthenticated || !session?.token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${API_BASE}${path}`, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.token}`,
          ...options.headers,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        const errorData = data as ApiError;
        throw new Error(errorData.error || `Request failed with status ${response.status}`);
      }

      return data as T;
    },
    [isAuthenticated, session?.token]
  );

  /**
   * List all venues for the authenticated user
   */
  const listVenues = useCallback(async (): Promise<Venue[]> => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiRequest<VenuesListResponse>('');
      setVenues(response.venues);
      return response.venues;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to list venues';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [apiRequest]);

  /**
   * Get a specific venue by ID
   */
  const getVenue = useCallback(async (id: string): Promise<Venue> => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiRequest<VenueResponse>(`/${id}`);
      setCurrentVenue(response.venue);
      return response.venue;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to get venue';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [apiRequest]);

  /**
   * Create a new venue
   * Accepts either just a title (simplified form) or full venue data
   */
  const createVenue = useCallback(
    async (data: NewVenueFormData | (VenueFormData & { hours?: DayHours[]; photos?: VenuePhoto[]; documents?: VenueDocument[]; ragUrls?: VenueRagUrl[] })): Promise<Venue> => {
      setLoading(true);
      setError(null);

      try {
        const response = await apiRequest<VenueResponse>('', {
          method: 'POST',
          body: JSON.stringify(data),
        });
        setCurrentVenue(response.venue);
        // Update the venues list
        setVenues((prev) => [...prev, response.venue]);
        return response.venue;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to create venue';
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [apiRequest]
  );

  /**
   * Update an existing venue
   */
  const updateVenue = useCallback(
    async (
      id: string,
      data: Partial<VenueFormData> & { hours?: DayHours[]; photos?: VenuePhoto[]; documents?: VenueDocument[]; ragUrls?: VenueRagUrl[] }
    ): Promise<Venue> => {
      setLoading(true);
      setError(null);

      try {
        const response = await apiRequest<VenueResponse>(`/${id}`, {
          method: 'PUT',
          body: JSON.stringify(data),
        });
        setCurrentVenue(response.venue);
        // Update the venues list
        setVenues((prev) =>
          prev.map((v) => (v.id === id ? response.venue : v))
        );
        return response.venue;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to update venue';
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [apiRequest]
  );

  /**
   * Delete a venue
   */
  const deleteVenue = useCallback(async (id: string): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      await apiRequest<{ success: boolean }>(`/${id}`, {
        method: 'DELETE',
      });
      // Remove from venues list
      setVenues((prev) => prev.filter((v) => v.id !== id));
      // Clear current venue if it was deleted
      if (currentVenue?.id === id) {
        setCurrentVenue(null);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete venue';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [apiRequest, currentVenue?.id]);

  /**
   * Upload a file (photo or document) to the vault
   */
  const uploadFile = useCallback(
    async (
      file: File,
      fileType: 'photo' | 'document',
      venueId?: string
    ): Promise<FileUploadResponse['file']> => {
      if (!isAuthenticated || !session?.token) {
        throw new Error('Not authenticated');
      }

      const formData = new FormData();
      formData.append('file', file);
      formData.append('fileType', fileType);
      if (venueId) {
        formData.append('venueId', venueId);
      }

      const response = await fetch(`${API_BASE}/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.token}`,
          // Don't set Content-Type - browser will set it with boundary for multipart
        },
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        const errorData = data as ApiError;
        throw new Error(errorData.error || `Upload failed with status ${response.status}`);
      }

      return (data as FileUploadResponse).file;
    },
    [isAuthenticated, session?.token]
  );

  const clearError = useCallback(() => setError(null), []);
  const clearCurrentVenue = useCallback(() => setCurrentVenue(null), []);

  return {
    venues,
    currentVenue,
    loading,
    error,
    listVenues,
    getVenue,
    createVenue,
    updateVenue,
    deleteVenue,
    uploadFile,
    clearError,
    clearCurrentVenue,
  };
}
